import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Search, Sparkles, Music, User, X } from 'lucide-react';
import { sortEventsByDay } from '../data/mockData';
import EventCard from '../components/EventCard';
import VenueCard from '../components/VenueCard';
import PurchaseDrawer from '../components/PurchaseDrawer';
import VenueEventsDrawer from '../components/VenueEventsDrawer';
import ProfileScreen from '../components/ProfileScreen';
import AllItemsScreen from '../components/AllItemsScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { eventId, slug } = useParams();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isVenueEventsOpen, setIsVenueEventsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [allItemsMode, setAllItemsMode] = useState(null); // 'events' | 'venues' | null
  const [events, setEvents] = useState([]);
  const [venuesData, setVenuesData] = useState([]);

  // Fetch catalog from backend (DB-backed, updates without redeploy)
  const fetchCatalog = useCallback(async () => {
    try {
      const [evRes, vRes] = await Promise.all([
        fetch(`${API}/events`, { cache: 'no-store' }),
        fetch(`${API}/venues`, { cache: 'no-store' }),
      ]);
      if (evRes.ok) setEvents(await evRes.json());
      if (vRes.ok) setVenuesData(await vRes.json());
    } catch (err) {
      console.error('Error fetching catalog:', err);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
    // Re-fetch when the tab becomes visible again (e.g. returning from /admin in another tab or after switching apps)
    const onVis = () => { if (document.visibilityState === 'visible') fetchCatalog(); };
    const onFocus = () => fetchCatalog();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCatalog]);

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState('');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    points: 0,
    total_points: 0,
    tier: 'MEMBER',
    golden_threshold: 1000,
  });

  // Check login on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const uid = localStorage.getItem('user_id');
    const uname = localStorage.getItem('user_name');
    if (token && uid) {
      setIsLoggedIn(true);
      setUserId(uid);
      setUserData(prev => ({ ...prev, name: uname || '' }));
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/user/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setUserData(data);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (isLoggedIn && userId) {
      fetchUserData();
    }
  }, [isLoggedIn, userId, fetchUserData]);

  const handleLogin = (data) => {
    setIsLoggedIn(true);
    setUserId(data.user_id);
    setUserData(prev => ({ ...prev, name: data.name }));
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    setIsLoggedIn(false);
    setUserId('');
    setUserData({ name: '', points: 0, total_points: 0, tier: 'MEMBER', golden_threshold: 1000 });
  };

  // Filter events and venues
  const filteredEvents = useMemo(() => {
    const sorted = sortEventsByDay(events);
    if (!searchQuery.trim()) return sorted;
    const query = searchQuery.toLowerCase();
    return sorted.filter(
      (event) =>
        event.name.toLowerCase().includes(query) ||
        (event.venue || '').toLowerCase().includes(query)
    );
  }, [searchQuery, events]);

  const filteredVenues = useMemo(() => {
    const sorted = [...venuesData].sort((a, b) => {
      if (a.inactive && !b.inactive) return 1;
      if (!a.inactive && b.inactive) return -1;
      return 0;
    });
    if (!searchQuery.trim()) return sorted;
    const query = searchQuery.toLowerCase();
    return sorted.filter(
      (venue) =>
        venue.name.toLowerCase().includes(query) ||
        (venue.location || '').toLowerCase().includes(query)
    );
  }, [searchQuery, venuesData]);

  const handleEventClick = (event) => {
    if (event.inactive) return;
    setSelectedEvent(event);
    setIsPurchaseOpen(true);
  };

  const handleVenueEvents = (venue) => {
    setSelectedVenue(venue);
    setIsVenueEventsOpen(true);
  };

  const handleVenueEventClick = (event) => {
    setIsVenueEventsOpen(false);
    setSelectedVenue(null);
    setTimeout(() => {
      setSelectedEvent(event);
      setIsPurchaseOpen(true);
    }, 300);
  };

  const closePurchase = () => {
    setIsPurchaseOpen(false);
    setSelectedEvent(null);
    fetchUserData();
    if (eventId || slug) navigate('/', { replace: true });
  };

  const closeVenueEvents = () => {
    setIsVenueEventsOpen(false);
    setSelectedVenue(null);
  };

  // Deep link: /event/:eventId or pretty /:slug opens the purchase drawer directly
  useEffect(() => {
    if (!(eventId || slug) || events.length === 0) return;
    let ev = null;
    if (eventId) {
      ev = events.find((e) => String(e.id) === String(eventId) && !e.inactive);
    } else if (slug) {
      ev = events.find((e) => e.slug === slug && !e.inactive);
    }
    if (ev) {
      setSelectedEvent(ev);
      setIsPurchaseOpen(true);
    } else {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, slug, events]);

  const progressToGolden = Math.min((userData.points / userData.golden_threshold) * 100, 100);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-8">
      {/* Header */}
      <div className="px-5 pt-8 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-white text-3xl font-bold" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}>
              {isLoggedIn && userData.name ? `Bienvenido, ${userData.name}` : 'Bienvenido/a'}
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">Clubbers Member</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ backgroundColor: '#F82C47' }} data-testid="tier-badge">
              <Star size={12} className="text-white fill-white" />
              <span className="text-white font-bold text-xs">{userData.tier}</span>
            </div>
            <button
              onClick={() => setIsProfileOpen(true)}
              className="glass-dark w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
              data-testid="profile-button"
              aria-label="Perfil"
            >
              <User size={16} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Points Banner */}
      <div className="px-5 mb-5">
        <div className="rounded-full py-3 px-5 flex items-center gap-3" style={{ backgroundColor: '#F82C47' }}>
          <Star size={20} className="text-white fill-white" />
          <span className="text-white font-bold text-sm" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif', fontWeight: 800 }}>
            CLUBBERS MEMBER
          </span>
          <span className="text-white font-bold text-2xl ml-auto" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}>
            {userData.points} pts
          </span>
        </div>
        <div className="mt-2 px-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-gray-500 text-xs font-medium">Progreso a Golden</span>
            <span className="text-gray-500 text-xs font-medium">{userData.points}/{userData.golden_threshold}</span>
          </div>
          <div className="w-full h-1.5 bg-[#2a2a2a] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progressToGolden}%`, backgroundColor: '#F82C47' }}
            />
          </div>
        </div>
      </div>

      {/* Search Bar — Apple Music style */}
      <div className="px-5 mb-6">
        <div className="flex items-center gap-2">
          <div className="glass-dark flex items-center gap-3 rounded-full px-4 py-2.5 flex-1">
            <Search size={18} className="text-white shrink-0" />
            <input
              type="text"
              placeholder="Busca clubs, eventos, viajes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-white placeholder-white/55 text-base flex-1 caret-[#F82C47]"
              style={{ WebkitAppearance: 'none', boxShadow: 'none' }}
              data-testid="home-search-input"
            />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="glass-dark w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 transition-transform active:scale-95"
              aria-label="Limpiar búsqueda"
              data-testid="home-search-clear"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Recommended Section */}
      {filteredEvents.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between px-5 mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-[#F82C47]" />
              <h2 className="text-white text-xl font-bold">Recomendados</h2>
            </div>
            <button className="text-[#F82C47] text-sm font-medium" onClick={() => setAllItemsMode('events')} data-testid="ver-mas-events">Ver más</button>
          </div>
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 scrollbar-hide">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} onClick={handleEventClick} />
            ))}
          </div>
        </div>
      )}

      {/* Venues Section */}
      {filteredVenues.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between px-5 mb-4">
            <div className="flex items-center gap-2">
              <Music size={20} className="text-[#F82C47]" />
              <h2 className="text-white text-xl font-bold">Venues</h2>
            </div>
            <button className="text-[#F82C47] text-sm font-medium" onClick={() => setAllItemsMode('venues')} data-testid="ver-mas-venues">Ver más</button>
          </div>
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 scrollbar-hide">
            {filteredVenues.map((venue) => (
              <VenueCard key={venue.id} venue={venue} onViewEvents={handleVenueEvents} />
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {searchQuery && filteredEvents.length === 0 && filteredVenues.length === 0 && (
        <div className="px-5 py-12 text-center">
          <Search size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No se encontraron resultados para "{searchQuery}"</p>
        </div>
      )}

      {/* Modals */}
      <PurchaseDrawer
        event={selectedEvent}
        isOpen={isPurchaseOpen}
        onClose={closePurchase}
        userId={userId}
        isLoggedIn={isLoggedIn}
        userName={userData.name}
        userEmail={userData.email}
      />
      <VenueEventsDrawer
        venue={selectedVenue}
        isOpen={isVenueEventsOpen}
        onClose={closeVenueEvents}
        onEventClick={handleVenueEventClick}
      />
      <ProfileScreen
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        userData={userData}
        onLogout={handleLogout}
        isLoggedIn={isLoggedIn}
        onLogin={handleLogin}
      />
      <AllItemsScreen
        isOpen={allItemsMode !== null}
        mode={allItemsMode}
        events={sortEventsByDay(events)}
        venues={venuesData}
        onClose={() => setAllItemsMode(null)}
        onEventClick={(event) => {
          setAllItemsMode(null);
          setTimeout(() => handleEventClick(event), 200);
        }}
        onVenueEvents={(venue) => {
          setAllItemsMode(null);
          setTimeout(() => handleVenueEvents(venue), 200);
        }}
      />
    </div>
  );
};

export default HomePage;
